import { useMemo, useState } from "react";
import {
  ChargerActionMenu,
  type CloseWsFeedback,
} from "../components/chargers/ChargerActionMenu";
import { ChargerControls } from "../components/chargers/ChargerControls";
import { ChargerLegend } from "../components/chargers/ChargerLegend";
import { ChargerLogPanel } from "../components/chargers/ChargerLogPanel";
import { ChargerTile } from "../components/chargers/ChargerTile";
import { useAuth } from "../context/AuthContext";
import { useDashboard } from "../context/DashboardContext";
import type { ChargerLogSource } from "../hooks/useChargerLog";
import type { ChargerFilterKey, ChargerSortDir, ChargerSortKey } from "../types/charger";
import { canBackfillChargerMv } from "../lib/access";
import {
  filterChargerStates,
  sortChargerStates,
} from "../utils/chargerFilterSort";
import { buildChargerTileStates } from "../utils/chargerTileState";
import { TEST_CHARGER_TILE } from "../utils/chargerMock";
import { CardFrame } from "./CardFrame";

type MenuState = { chargerId: string; x: number; y: number };
type PanelState = { chargerId: string; source: ChargerLogSource };

type CloseWsResponse = {
  ok?: boolean;
  status?: string;
  message?: string;
  error?: string;
};

function menuPosition(ev: React.MouseEvent<HTMLButtonElement>) {
  const rect = ev.currentTarget.getBoundingClientRect();
  const x = Math.min(rect.left, window.innerWidth - 220);
  const y = Math.min(rect.bottom + 4, window.innerHeight - 220);
  return { x: Math.max(8, x), y: Math.max(8, y) };
}

function closeWsFeedbackFromResponse(body: CloseWsResponse): CloseWsFeedback {
  if (body.error) {
    return { kind: "error", message: body.error };
  }
  const status = body.status ?? "";
  const message = body.message ?? "Close request finished";
  if (status === "closed") {
    return { kind: "success", message };
  }
  if (status === "not_connected") {
    return { kind: "warning", message };
  }
  if (status === "sent_unconfirmed") {
    return { kind: "warning", message };
  }
  if (status === "error") {
    return { kind: "error", message };
  }
  return body.ok
    ? { kind: "success", message }
    : { kind: "warning", message };
}

export function ConnectedChargersRow({ compact = false }: { compact?: boolean }) {
  const { user } = useAuth();
  const { connectedChargers, lbInFcr } = useDashboard();
  const allowBackfill = canBackfillChargerMv(user);
  const ids = connectedChargers?.charger_ids ?? [];
  const lbIds = useMemo(() => {
    if (!lbInFcr?.loaded) return null;
    return new Set(lbInFcr.ids);
  }, [lbInFcr]);
  const allStates = useMemo(
    () => buildChargerTileStates(ids, connectedChargers?.health, connectedChargers?.tiles, lbIds),
    [ids, connectedChargers?.health, connectedChargers?.tiles, lbIds],
  );

  const [filters, setFilters] = useState<Set<ChargerFilterKey>>(new Set());
  const [sortKey, setSortKey] = useState<ChargerSortKey>("id");
  const [sortDir, setSortDir] = useState<ChargerSortDir>("asc");
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [panel, setPanel] = useState<PanelState | null>(null);
  const [closingWsId, setClosingWsId] = useState<string | null>(null);
  const [closeWsFeedback, setCloseWsFeedback] = useState<CloseWsFeedback | null>(null);
  const [backfilling, setBackfilling] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const searchActive = normalizedSearch.length > 0;

  const chargerMatchesSearch = (id: string, label: string) => {
    if (!searchActive) return false;
    return `${id} ${label}`.toLowerCase().includes(normalizedSearch);
  };

  const handleBackfill = async () => {
    if (!allowBackfill || backfilling) return;
    setBackfilling(true);
    try {
      const res = await fetch("/api/ocpp/charger-mv/backfill", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        updated?: string[];
        targets?: string[];
      };
      if (!res.ok) throw new Error(body.error || `Backfill failed (${res.status})`);
    } catch (err) {
      console.warn("charger MV backfill:", err);
    } finally {
      setBackfilling(false);
    }
  };

  const handleCloseWs = async (chargerId: string) => {
    if (
      !window.confirm(
        `Close the WebSocket for ${chargerId}? The charger will need to reconnect.`,
      )
    ) {
      return;
    }
    setCloseWsFeedback(null);
    setClosingWsId(chargerId);
    try {
      const res = await fetch("/api/ocpp/charger-close-ws", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ charger_id: chargerId }),
      });
      const body = (await res.json().catch(() => ({}))) as CloseWsResponse;
      if (!res.ok) {
        throw new Error(body.error || `Close failed (${res.status})`);
      }
      setCloseWsFeedback(closeWsFeedbackFromResponse(body));
    } catch (err) {
      setCloseWsFeedback({
        kind: "error",
        message: err instanceof Error ? err.message : "Close failed",
      });
    } finally {
      setClosingWsId(null);
    }
  };

  const visibleStates = useMemo(() => {
    const filtered = filterChargerStates(allStates, filters);
    return sortChargerStates(filtered, sortKey, sortDir);
  }, [allStates, filters, sortKey, sortDir]);

  const filteredLabels = useMemo(
    () => visibleStates.map((s) => s.label),
    [visibleStates],
  );

  const searchMatchCount = useMemo(() => {
    if (!searchActive) return null;
    return visibleStates.filter((s) => chargerMatchesSearch(s.id, s.label)).length;
  }, [visibleStates, searchActive, normalizedSearch]);

  if (!ids.length) {
    return (
      <div className="flex w-full flex-col gap-2">
        <ChargerLegend />
        <div className={compact ? "mobile-charger-grid w-full" : "flex w-full flex-wrap gap-1.5"}>
          <ChargerTile state={TEST_CHARGER_TILE} />
        </div>
        <CardFrame
          className="w-full px-4 py-4 text-text-secondary"
          style={{ fontSize: "var(--font-body)" }}
        >
          No chargers connected
        </CardFrame>
      </div>
    );
  }

  return (
    <>
      <div className="flex w-full flex-col gap-2">
        <ChargerLegend />
        <ChargerControls
          filteredCount={visibleStates.length}
          totalCount={allStates.length}
          filteredLabels={filteredLabels}
          filters={filters}
          sortKey={sortKey}
          sortDir={sortDir}
          searchQuery={searchQuery}
          searchMatchCount={searchMatchCount}
          onSearchChange={setSearchQuery}
          onFiltersChange={setFilters}
          onSortChange={(key, dir) => {
            setSortKey(key);
            setSortDir(dir);
          }}
        />
        {allowBackfill && (
          <button
            type="button"
            onClick={() => void handleBackfill()}
            disabled={backfilling}
            className="self-end text-text-tertiary opacity-40 transition-opacity hover:opacity-70 disabled:opacity-30"
            style={{ fontSize: "var(--font-micro)" }}
            title="Rebuild charger tile state from today's OCPP log (admin)"
          >
            {backfilling ? "…" : "↺ state"}
          </button>
        )}

        {visibleStates.length === 0 ? (
          <div className="flex flex-col gap-2">
            <div className={compact ? "mobile-charger-grid w-full" : "flex w-full flex-wrap gap-1.5"}>
              <ChargerTile state={TEST_CHARGER_TILE} />
            </div>
            <p className="text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
              No chargers match the current filters.
            </p>
          </div>
        ) : (
          <div className={compact ? "mobile-charger-grid w-full" : "flex w-full flex-wrap gap-1.5"}>
            <ChargerTile state={TEST_CHARGER_TILE} />
            {visibleStates.map((state) => {
              const matches = chargerMatchesSearch(state.id, state.label);
              return (
              <ChargerTile
                key={state.id}
                state={state}
                highlighted={searchActive && matches}
                dimmed={searchActive && !matches}
                onClick={(ev) => {
                  setCloseWsFeedback(null);
                  if (ev.shiftKey) {
                    setMenu(null);
                    setPanel({ chargerId: state.label, source: "ocpp" });
                    return;
                  }
                  setPanel(null);
                  setMenu({ chargerId: state.label, ...menuPosition(ev) });
                }}
              />
            );
            })}
          </div>
        )}
      </div>

      {menu && (
        <ChargerActionMenu
          chargerId={menu.chargerId}
          anchor={{ x: menu.x, y: menu.y }}
          onOcpp={() => setPanel({ chargerId: menu.chargerId, source: "ocpp" })}
          onWs={() => setPanel({ chargerId: menu.chargerId, source: "ws" })}
          onCloseWs={() => void handleCloseWs(menu.chargerId)}
          closingWs={closingWsId === menu.chargerId}
          closeWsFeedback={closeWsFeedback}
          onClose={() => {
            setMenu(null);
            setCloseWsFeedback(null);
          }}
        />
      )}

      {panel && (
        <ChargerLogPanel
          chargerId={panel.chargerId}
          source={panel.source}
          onClose={() => setPanel(null)}
          onChargerIdChange={(id) => setPanel((prev) => (prev ? { ...prev, chargerId: id } : null))}
        />
      )}
    </>
  );
}
